/**
 * 验证码输入（PRD §8.5「输入」、§8.7「验证」；platform REQ-8.7-011）：
 * - 一个输入框收全部位数：调出数字键盘（inputmode=numeric），支持系统自动填充（autocomplete=one-time-code）；
 * - 支持粘贴：从粘贴的整段文字里取出连续的 6 位数字（例如邮件里的"验证码 123456，5 分钟内有效"），全角数字自动转半角；
 * - 输满位数时回调 onComplete（由调用方决定是否自动提交）。
 * 防骗提示、倒计时与重发由身份领域的 OtpInput 组合本组件实现；Passkey 代替验证码的入口见应用层的 PasskeyGate。
 */
import { forwardRef, type ClipboardEvent, type InputHTMLAttributes } from 'react';
import { cx } from '../cx.js';
import { INPUT_CLASS, useFieldControl } from './Field.js';

const FULL_WIDTH_DIGIT = /[０-９]/g;

function toAsciiDigits(text: string): string {
  return text.replace(FULL_WIDTH_DIGIT, (d) => String.fromCharCode(d.charCodeAt(0) - 0xff10 + 0x30));
}

/** 从任意文字中取出验证码：优先取恰好 length 位的连续数字，否则去掉非数字后截取前 length 位。 */
export function extractCode(text: string, length = 6): string {
  const ascii = toAsciiDigits(text);
  const exact = new RegExp(`(?<!\\d)\\d{${length}}(?!\\d)`).exec(ascii);
  if (exact) return exact[0];
  return ascii.replace(/\D/g, '').slice(0, length);
}

export interface CodeInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'inputMode' | 'autoComplete' | 'maxLength' | 'pattern'> {
  value: string;
  onChange: (code: string) => void;
  /** 输满 length 位时调用 */
  onComplete?: (code: string) => void;
  /** 位数，默认 6（PRD §8.2） */
  length?: number;
}

export const CodeInput = forwardRef<HTMLInputElement, CodeInputProps>(function CodeInput(
  { value, onChange, onComplete, length = 6, className, onPaste, ...rest },
  ref,
) {
  const control = useFieldControl();
  const update = (next: string) => {
    if (next === value) return;
    onChange(next);
    if (next.length === length && value.length !== length) onComplete?.(next);
  };
  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    onPaste?.(event);
    if (event.defaultPrevented) return;
    const text = event.clipboardData?.getData('text') ?? '';
    const code = extractCode(text, length);
    if (!code) return;
    event.preventDefault();
    update(code);
  };
  return (
    <input
      ref={ref}
      {...control}
      {...rest}
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      pattern={`[0-9]{${length}}`}
      maxLength={length}
      spellCheck={false}
      value={value}
      onChange={(event) => update(toAsciiDigits(event.target.value).replace(/\D/g, '').slice(0, length))}
      onPaste={handlePaste}
      className={cx(INPUT_CLASS, 'num text-center text-2xl tracking-[0.4em]', className)}
    />
  );
});
